import React from 'react'
import { Join } from '../../lib/utils'

/**
 * Layout primitives for arranging items inside a `<Container>`.
 *
 * - `<Stack>` — vertical flex column with a configurable gap.
 * - `<CardGrid>` — responsive grid (1 col mobile → up to N cols desktop).
 * - `<SplitTwoCol>` — two-column split (1 col mobile, 2 cols md+).
 * - `<CTARow>` — horizontal cluster of CTAs / buttons, centered & wrapping.
 * - `<ChipRow>` — horizontal cluster of tags/chips, wrapping.
 *
 * All server-component safe.
 */

type GapSize = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const gapClass: Record<GapSize, string> = {
  none: 'gap-0',
  xs: 'gap-2',
  sm: 'gap-3',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
}

// ── Stack ───────────────────────────────────────────────────────────────────

export interface StackProps {
  children: React.ReactNode
  /** Gap between children. Default `md`. */
  gap?: GapSize
  /** Cross-axis alignment. Default `stretch`. */
  align?: 'start' | 'center' | 'end' | 'stretch'
  className?: string
  id?: string
  as?: keyof React.JSX.IntrinsicElements
}

const alignClass = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
} as const

export function Stack({
  children,
  gap = 'md',
  align = 'stretch',
  className,
  id,
  as = 'div',
}: StackProps) {
  return React.createElement(
    as,
    {
      id,
      className: Join(
        'flex flex-col',
        gapClass[gap],
        alignClass[align],
        className,
      ),
    },
    children,
  )
}

// ── CardGrid ────────────────────────────────────────────────────────────────

export interface CardGridProps {
  children: React.ReactNode
  /** Maximum columns on `lg` screens. Default 3. */
  columns?: 1 | 2 | 3 | 4
  gap?: GapSize
  className?: string
  id?: string
}

const colClass = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
} as const

export function CardGrid({
  children,
  columns = 3,
  gap = 'lg',
  className,
  id,
}: CardGridProps) {
  return (
    <div
      id={id}
      className={Join(
        'grid grid-cols-1',
        colClass[columns],
        gapClass[gap],
        className,
      )}
    >
      {children}
    </div>
  )
}

// ── SplitTwoCol ─────────────────────────────────────────────────────────────

export interface SplitTwoColProps {
  left: React.ReactNode
  right: React.ReactNode
  /** Reverse column order (right rendered first). Default false. */
  reverse?: boolean
  /** Column ratio. `equal` = 1:1, `wideLeft` = 2:1, `wideRight` = 1:2. */
  ratio?: 'equal' | 'wideLeft' | 'wideRight'
  gap?: GapSize
  className?: string
  id?: string
}

const ratioClass = {
  equal: 'md:grid-cols-2',
  wideLeft: 'md:grid-cols-[2fr_1fr]',
  wideRight: 'md:grid-cols-[1fr_2fr]',
} as const

export function SplitTwoCol({
  left,
  right,
  reverse = false,
  ratio = 'equal',
  gap = 'lg',
  className,
  id,
}: SplitTwoColProps) {
  return (
    <div
      id={id}
      className={Join(
        'grid grid-cols-1',
        ratioClass[ratio],
        gapClass[gap],
        className,
      )}
    >
      <div className={reverse ? 'md:order-2' : ''}>{left}</div>
      <div className={reverse ? 'md:order-1' : ''}>{right}</div>
    </div>
  )
}

// ── CTARow ──────────────────────────────────────────────────────────────────

export interface CTARowProps {
  children: React.ReactNode
  /** Justification. Default `center`. */
  justify?: 'start' | 'center' | 'end'
  gap?: GapSize
  className?: string
}

const justifyClass = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
} as const

export function CTARow({
  children,
  justify = 'center',
  gap = 'sm',
  className,
}: CTARowProps) {
  return (
    <div
      className={Join(
        'flex flex-wrap items-center',
        justifyClass[justify],
        gapClass[gap],
        className,
      )}
    >
      {children}
    </div>
  )
}

// ── ChipRow ─────────────────────────────────────────────────────────────────

export interface ChipRowProps {
  children: React.ReactNode
  gap?: GapSize
  className?: string
}

export function ChipRow({ children, gap = 'xs', className }: ChipRowProps) {
  return (
    <div
      className={Join('flex flex-wrap items-center', gapClass[gap], className)}
    >
      {children}
    </div>
  )
}
